<script lang="ts">
/*
EventLogic listens for bot activities and writes UI events into Redux.
*/
import Vue from 'vue'
// packages
import { mapGetters, mapActions } from 'vuex'
import { Component } from 'vue-property-decorator'
// types
import { logVueModalUsage } from '@/common/utils'
import { ActivityValue, routeModalActivity } from '@/events/modal-activity'
import { progressUpdated } from '@/store/modules/progress'
import {
  configUpdated,
  modalHidden,
  modelUpdated,
  overlayUpdated,
  screenSizeChanged,
  themeUpdated,
} from '@/store/modules/bot-events'
import { reduxStore } from '@/store/store'

const logModalRequested = (model: ActivityValue): void => {
  logVueModalUsage('Modal requested', {
    modal: model.modal,
    displayType: model.type,
    view: model.view,
  })
}

@Component({
  computed: {
    ...mapGetters({
      botConnection: 'webchat/botConnection',
      store: 'webchat/store',
      token: 'webchat/token',
    }),
  },
  methods: {
    ...mapActions({
      registerActivityCallback: 'webchat/registerActivityCallback',
      sendEvent: 'webchat/sendEvent',
      sendMessage: 'webchat/sendMessage',
      expireToken: 'user/expireToken',
    }),
  },
})
export default class EventLogic extends Vue {
  // getters
  botConnection!: any
  store!: any
  token!: any
  // methods
  registerActivityCallback!: any
  sendEvent!: any
  sendMessage!: any
  expireToken!: any

  public messageTimeout: any
  public model: object = {}
  public theme: any = {}
  public config: object = {}
  public showModal = false
  public showOverlay = false
  public screenSize: number = window.innerWidth
  public open = false

  public getModel(): void {
    reduxStore.dispatch(modelUpdated(this.model))
  }
  public pushOverlay(): void {
    reduxStore.dispatch(overlayUpdated(this.model))
  }
  public openOverlay(model: ActivityValue): void {
    this.model = model
    this.showModal = false
    this.showOverlay = true
    logModalRequested(model)
    this.pushOverlay()
  }
  public hideModal(): void {
    this.showModal = false
    this.showOverlay = false
    reduxStore.dispatch(modalHidden())
  }
  public getTheme(): void {
    reduxStore.dispatch(themeUpdated(this.theme))
  }
  public getConfig(): void {
    reduxStore.dispatch(configUpdated(this.config))
  }
  public getScreenSize(): void {
    reduxStore.dispatch(screenSizeChanged(this.screenSize))
  }
  public getWindowWidth(): void {
    this.screenSize = window.innerWidth
    this.getScreenSize()
  }
  created() {
    //var vm = this;
    const modalActivityCallback = async (activity: any) => {
      const { name, type, value } = activity
      const routed = routeModalActivity(value)
      if (type === 'event') {
        if (name === 'conversation.stop') {
          await this.expireToken()
          this.$router.replace({ name: 'login' })
        } else if (
          name === 'conversation.subject.does.not.exist' ||
          name === 'conversation.subject.has.expired' ||
          name === 'conversation.subject.cancelled'
        ) {
          if (this.$route.name !== 'complete') {
            await this.expireToken()
            this.$router.replace({ name: 'complete' })
          }
        } else if (name === 'input.mode') {
          const inputs = document.querySelectorAll('[data-id=webchat-sendbox-input]')

          if (inputs.length > 0) {
            inputs[0].setAttribute('inputmode', value)
          }
        } else if (name && name.search(/inactivity.message/gim) === 0) {
          if (name.search(/schedule/gim) > 0) {
            this.messageTimeout = setTimeout(
              () => this.sendEvent({ name: 'inactivity.message.show' }),
              30000,
            )
          } else if (name.search(/cancel/gim) > 0) {
            clearTimeout(this.messageTimeout)
          }
        } else if (name && name.search(/oauth/gim) === 0) {
          if (name.search(/cancel/gim) > 0) {
            // TODO: ADD BACK IN WHEN CANCELLABLE ACTIONS ARE WORKING
          }
        } else if (routed?.action === 'overlay') {
          this.openOverlay(routed.model)
        } else if (routed?.action === 'ignore') {
          logModalRequested(routed.model)
        } else if (name === 'progressUpdate') {
          const { recommendations, flows } = value
          if (!recommendations || !flows) {
            throw new Error('Invalid progress object! Cannot update UI.')
          } else {
            reduxStore.dispatch(progressUpdated({ recommendations, flows }))
          }
        } else if (name === 'themeAndConfigUpdate') {
          this.theme = { ...(value.themes[0] || {}) }
          this.config = { ...value }
          this.getConfig()
          this.getTheme()
        } else if (name === 'restart.action' || name === 'hide.overlay') {
          this.model = {}
          this.showOverlay = false
          this.pushOverlay()
          this.getModel()
          this.hideModal()
        } else if (name === 'redirect') {
          window.location.href = value
        }
      }
    }
    this.registerActivityCallback(modalActivityCallback)
    window.addEventListener('resize', this.getWindowWidth)
  }
  render() {
    // gets rid of console error, needs to be defined even if there is nothing to render
    return null
  }
  destroyed() {
    window.removeEventListener('resize', this.getWindowWidth)
  }
}
</script>
